/**
 * The video plan — what the video agent writes and the scene library renders, in the browser (Remotion Player) and
 * to MP4 (Remotion renderer). The backend keeps the same shape in src/engine/video/spec.ts; change both together.
 *
 * Times are in seconds (the agent thinks in seconds); the composition converts to frames at VIDEO_FPS.
 * This folder is bundled on its own for rendering, so it imports nothing from the app.
 */

export type VideoAspect = '9:16' | '1:1' | '4:5' | '16:9';
export type VideoTemplate = 'creator-ad' | 'brand-motion' | 'campaign-recap' | 'blank';
export type SceneTone = 'dark' | 'light' | 'brand';
export type SceneTransition = 'cut' | 'fade' | 'slide' | 'wipe';

export const VIDEO_FPS = 30;
/** Frames two scenes overlap during a fade, slide or wipe (≤ 0.3 s: short ads cut, they don't dissolve). */
export const TRANSITION_FRAMES = 8;

export const ASPECT_SIZE: Record<VideoAspect, { width: number; height: number }> = {
  '9:16': { width: 1080, height: 1920 },
  '1:1': { width: 1080, height: 1080 },
  '4:5': { width: 1080, height: 1350 },
  '16:9': { width: 1920, height: 1080 },
};

export interface VideoTheme {
  brandName: string;
  /** Light ground and dark ink; the accent is the brand colour. */
  bg: string;
  ink: string;
  accent: string;
  accent2?: string | null;
  headingFont: string;
  bodyFont: string;
  /** A Google Fonts css2 link carrying both faces, when they are on Google Fonts. */
  fontHref?: string | null;
  /** Logo for light grounds, and one for dark grounds (either may be the only one). */
  logoUrl?: string | null;
  logoOnDarkUrl?: string | null;
}

export interface VideoMedia {
  url: string;
  kind: 'video' | 'image';
  posterUrl?: string | null;
  /** Seconds into the source video where this scene starts. */
  trimStart?: number;
  /** 0–1; creator clips keep their own sound by default. */
  volume?: number;
  fit?: 'cover' | 'contain';
  /** Vertical focus of a cover crop, 0 (top) – 100 (bottom). */
  focusY?: number;
  /** Horizontal focus of a cover crop (0 left – 100 right), set by the layout pass from where the faces are. */
  focusX?: number;
  /** Where the media came from (Content Hub id, upload, generated) — for rights and re-use. */
  sourceId?: string | null;
  creatorHandle?: string | null;
  /** Clip speed (0.25–4). The scene's seconds are screen time: it plays duration × rate seconds of the source. */
  playbackRate?: number;
  /** The source's pixel size, when known — the renderer frames footage that would crop badly instead of zooming it. */
  w?: number;
  h?: number;
  /** The creator's display name, for the card header ("Jenny Hagen · @jennynhagen"). */
  creatorName?: string | null;
}

/**
 * Where the video runs — facts that change the rules, not the concept:
 *   social  feeds (TikTok, Reels, Shorts, feed): sound-off first, captions on, frame 0 is the thumbnail, platform safe zones.
 *   ctv     a TV (connected TV): sound ON, nobody scrolls or clicks, text read from a sofa, 15/20/30 s spots,
 *           broadcast loudness (−23 LUFS), title-safe 90 %.
 *   web     landscape players (YouTube, LinkedIn, sites): sound often on, clickable, −14 LUFS.
 */
export type VideoChannel = 'social' | 'ctv' | 'web';
export const channelOf = (spec: Pick<VideoSpec, 'channel' | 'aspect'>): VideoChannel => spec.channel || (spec.aspect === '16:9' ? 'web' : 'social');

/**
 * The brand stage — a panel that stays on screen while the scenes play beside it (landscape): logo, one claim, the
 * products, and the legal line when the brand has one. Scenes inside its span play in the rest of the frame, creator
 * footage as a post-like card. Default span: from the start to the end card (or the end).
 */
export interface BrandStage {
  side?: 'left' | 'right';
  claim?: string;
  /** Packshots shown together (≤ 4), on the brand ground. */
  products?: VideoMedia[];
  /** Small print at the bottom of the panel (regulated claims: SPF, alcohol, finance…). */
  legal?: string;
  from?: number;
  to?: number;
}

/** One voice-over clip: plays `seconds` of `url` from `from` (s into the file), starting `at` s into the video. */
/**
 * A text on its own layer (detached from any clip): it can span cuts. `scene` set = linked — it moves with that clip
 * when clips move or change length; unset = it stays at its time.
 */
export interface TextLayer { id: string; text: string; at: number; seconds: number; band?: 'top' | 'middle' | 'bottom'; side?: 'left' | 'center' | 'right'; scene?: string }
/**
 * The captions layer: everything heard (creators kept in the cut, the voice-over), transcribed to the word. Kept per
 * SOURCE file (a creator's video, a voice-over take) in source seconds — the video shows the lines inside each clip's
 * window, so moving, trimming, splitting or re-timing a clip never needs a new transcript. `on` false hides the layer.
 */
export interface TranscriptWord { w: string; s: number; e: number }
export interface TranscriptLine { s: number; e: number; text: string; words?: TranscriptWord[] }
export interface TranscriptSource { language?: string; /** The creator burned their own subtitles in: ours stay hidden there. */ burned?: boolean; by?: 'scribe' | 'whisper' | 'gemini' | 'edited'; lines: TranscriptLine[] }
export interface VideoTranscript { on: boolean; sources: Record<string, TranscriptSource> }
export interface VoicePart { id: string; url: string; at: number; from?: number; seconds: number; text?: string; voice?: string; pace?: 'calm' | 'normal' | 'fast'; scene?: string }

export interface Caption {
  text: string;
  /** Seconds, relative to the scene start. */
  start: number;
  end: number;
}

interface SceneBase {
  id: string;
  /** Seconds on screen. */
  duration: number;
  /** How this scene comes in (the first scene always cuts in). */
  transition?: SceneTransition;
  tone?: SceneTone;
  /** The person's text size for this scene (0.7–1.4 × the library's own size). */
  textScale?: number;
}

/**
 * The first 1–2 seconds: the line that stops the scroll. Its FIRST FRAME is what the feed shows (autoplay, sound
 * off, the thumbnail), so every hook motion except 'rise' has the line readable from frame 0.
 *   motion: punch (the line is there and lands with a scale punch — default), slam (lands big with a jolt),
 *           type (word by word, fast, first word at frame 0), rise (words float in — the only one with an empty
 *           first frame; use only when the footage itself carries frame 0).
 *   opening: blur (the line over a blurred, darkened clip — type-led hooks), live (the clip plays sharp and the line
 *           sits in a solid box — when the footage IS the hook: a reaction, a demo, a before/after).
 *   tactic: the hook tactic from the creative taxonomy (pain-callout, result-first, pov…), kept for learning.
 */
export type HookMotion = 'punch' | 'slam' | 'type' | 'rise';
export interface HookScene extends SceneBase { kind: 'hook'; text: string; sub?: string; media?: VideoMedia | null; emphasis?: string[]; motion?: HookMotion; opening?: 'blur' | 'live'; tactic?: string; place?: Placement; voice?: 'keep' | 'mute' }
/**
 * Where text sits on footage, chosen by the layout pass from what is in the frames (faces, the product, the action,
 * text the creator burned in): the band and side that cover the least of it across the scene.
 */
export interface Placement { overlay?: 'top' | 'middle' | 'bottom'; side?: 'left' | 'center' | 'right'; captions?: 'top' | 'middle' | 'bottom' }
/** A creator video, trimmed, with burned-in captions and an optional overlay line. */
/** voice: keep the creator's own sound (they speak in this window) or mute it (the bed carries the scene). */
export interface ClipScene extends SceneBase { kind: 'clip'; media: VideoMedia; captions?: Caption[]; overlay?: string; showHandle?: boolean; place?: Placement; voice?: 'keep' | 'mute'; /** More creators side by side with this one (a split: ≤ 2 more, muted — `media` is the one heard). */ others?: VideoMedia[] }
/** Kinetic type: one statement. */
export interface TextScene extends SceneBase { kind: 'text'; text: string; eyebrow?: string; sub?: string; emphasis?: string[] }
/** One big number. */
export interface StatScene extends SceneBase { kind: 'stat'; value: string; label: string; sub?: string; media?: VideoMedia | null }
/** A quote (a comment, a review, a creator line). */
export interface QuoteScene extends SceneBase { kind: 'quote'; quote: string; author?: string; media?: VideoMedia | null }
/** A product shot with its name and one line. */
export interface ProductScene extends SceneBase { kind: 'product'; media: VideoMedia; name: string; line?: string }
/** Logo, headline, call to action. */
export interface EndCardScene extends SceneBase { kind: 'endcard'; headline?: string; cta?: string; url?: string; /** The packshot next to the button (sales ads). */ media?: VideoMedia | null }

export type VideoScene = HookScene | ClipScene | TextScene | StatScene | QuoteScene | ProductScene | EndCardScene;
export type SceneKind = VideoScene['kind'];

/**
 * The soundtrack, designed once for the whole video (the sound pass): who leads (creators' voices, the music, or a
 * voice-over), one music bed under everything that ducks under speech, and light sound design.
 */
export interface VideoAudio {
  mode?: 'creator' | 'music' | 'voiceover';
  musicUrl?: string | null;
  /** Bed level when nobody speaks (0–1). */
  musicVolume?: number;
  /** Tempo of the bed, for cutting on the beat. */
  bpm?: number;
  voiceoverUrl?: string | null;
  voiceoverVolume?: number;
  /** Where the recorded voice-over starts (s into the video) and how long it is. */
  voiceoverStart?: number;
  voiceoverSeconds?: number;
  /** An AI voice-over keeps its script, voice and pace (editable later; the voice is re-made from them). */
  voiceoverScript?: string;
  voiceoverVoice?: string;
  voiceoverPace?: 'calm' | 'normal' | 'fast';
  /** The voice-over as clips on its track (split, moved, one line per scene). Wins over the single-take fields. */
  voParts?: VoicePart[];
  /** Spans (seconds, video time) where someone speaks: the music ducks under them with short ramps. */
  duck?: Array<{ start: number; end: number }>;
  /** One-shot sounds: a whoosh on a slide, a soft hit on the product reveal. */
  /** manual: placed by a person (kept when the sound is redone); generated effects are off by default. */
  sfx?: Array<{ at: number; url: string; volume?: number; manual?: boolean; /** Linked to this clip: moves with it. */ scene?: string }>;
  /** Where frame 0 enters the track (s) — past a full-length track's intro, on a downbeat. */
  musicStart?: number;
  musicTitle?: string;
  musicSource?: 'picked' | 'studio' | 'library' | 'generated';
  musicBrief?: string;
  genre?: string; mood?: string; tempo?: 'slow' | 'medium' | 'fast';
}

/**
 * The video's art direction, set once (the deck machine's rule): a mood, and ONE motif every scene carries so the
 * video feels like one piece — rule = an accent bar above each headline; bug = the logo small in a corner throughout;
 * frame = a thin accent border round the whole video; none.
 */
export type VideoMotif = 'rule' | 'bug' | 'frame' | 'none';
export interface VideoArt { mood?: string; motif?: VideoMotif }

export interface VideoSpec {
  v: 1;
  /** The video's one language (ISO 639-1): every line, the hook and the captions (translated when the creator speaks another). */
  language?: string;
  art?: VideoArt;
  title: string;
  template: VideoTemplate;
  aspect: VideoAspect;
  theme: VideoTheme;
  scenes: VideoScene[];
  audio?: VideoAudio;
  /** Caption look for clip scenes: bold = word-by-word social style, clean = a quiet subtitle line. */
  captionStyle?: 'bold' | 'clean';
  /** The creative concept it was made with (backend concepts.ts) — direction, recorded so results compare by concept. */
  concept?: string;
  /** Where it runs (see VideoChannel); unset = social for portrait/square, web for 16:9. */
  channel?: VideoChannel;
  /** The brand stage (landscape): a panel that stays while the scenes play beside it. */
  stage?: BrandStage | null;
  /** Texts on their own layer, over the scenes (see TextLayer). */
  texts?: TextLayer[];
  /** The captions layer: every heard voice, to the word (see VideoTranscript). */
  transcript?: VideoTranscript;
}

export const secondsToFrames = (s: number) => Math.max(1, Math.round(s * VIDEO_FPS));

/** Frames a scene's incoming transition overlaps the previous scene (0 for the first scene and for cuts). */
export function overlapFrames(scenes: VideoScene[], i: number): number {
  if (i === 0) return 0;
  const t = scenes[i].transition || 'fade';
  if (t === 'cut') return 0;
  const shorter = Math.min(secondsToFrames(scenes[i - 1].duration), secondsToFrames(scenes[i].duration));
  return Math.min(TRANSITION_FRAMES, Math.floor(shorter / 2));
}

export function totalFrames(spec: Pick<VideoSpec, 'scenes'>): number {
  if (!spec.scenes.length) return VIDEO_FPS;
  return spec.scenes.reduce((sum, s, i) => sum + secondsToFrames(s.duration) - overlapFrames(spec.scenes, i), 0);
}

/** Frame where each scene starts in the finished video (after transition overlaps). */
export function sceneStarts(spec: Pick<VideoSpec, 'scenes'>): number[] {
  const starts: number[] = [];
  let at = 0;
  spec.scenes.forEach((s, i) => { at -= overlapFrames(spec.scenes, i); starts.push(at); at += secondsToFrames(s.duration); });
  return starts;
}

export const DEFAULT_THEME: VideoTheme = {
  brandName: 'Beatly',
  bg: '#FAF7FB',
  ink: '#2E024C',
  accent: '#A9F600',
  accent2: '#FF9CFF',
  headingFont: 'Bricolage Grotesque',
  bodyFont: 'Instrument Sans',
  fontHref: 'https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:wght@400..800&family=Instrument+Sans:wght@400..700&display=swap',
  logoUrl: null,
  logoOnDarkUrl: null,
};

/** The voice-over clips — `voParts`, or the one take older plans keep in the single-take fields. */
export function voicePartsOf(audio?: VideoAudio): VoicePart[] {
  if (!audio) return [];
  if (audio.voParts?.length) return audio.voParts;
  if (audio.voiceoverUrl) return [{ id: 'vo', url: audio.voiceoverUrl, at: audio.voiceoverStart || 0, from: 0, seconds: audio.voiceoverSeconds || 30, text: audio.voiceoverScript, voice: audio.voiceoverVoice, pace: audio.voiceoverPace }];
  return [];
}

/** The brand stage's span in seconds: [from, to). Default: from 0 to the end card (or the end). */
export function stageSpan(spec: Pick<VideoSpec, 'scenes' | 'stage'>): [number, number] | null {
  if (!spec.stage) return null;
  const starts = sceneStarts(spec).map((f) => f / VIDEO_FPS);
  // The closing frame says one thing: the panel steps aside for the end card and for closing text scenes after the
  // last footage (beside the panel, the logo and product would show twice).
  let end = spec.scenes.findIndex((s) => s.kind === 'endcard');
  let close = spec.scenes.length;
  while (close > 1 && ['endcard', 'text'].includes(spec.scenes[close - 1].kind)) close--;
  if (close < spec.scenes.length && (end < 0 || close < end)) end = close;
  const total = totalFrames(spec) / VIDEO_FPS;
  const to = spec.stage.to ?? (end > 0 ? starts[end] : total);
  return [Math.max(0, spec.stage.from ?? 0), Math.max(0, to)];
}

/** Width over height of a clip or picture; creator videos without a known size are taken as phone-vertical. */
export function mediaAspect(m: Pick<VideoMedia, 'w' | 'h' | 'kind'>): number | null {
  if (m.w && m.h) return m.w / m.h;
  return m.kind === 'video' ? 9 / 16 : null;
}
/** How far a cover fill has to zoom to put footage of `media` aspect into a box of `box` aspect. */
export const coverZoom = (media: number, box: number) => Math.max(media / box, box / media);
/**
 * The footage rule: a fill may zoom at most this much — past it, the picture loses its subject (a 9:16 creator clip
 * filling a 16:9 frame zooms 3.2×: a blurry slice of a shoe). Beyond it the footage is framed, never cropped.
 */
export const MAX_COVER_ZOOM = 1.35;
